import React, {
  useState, useRef
} from 'react';
import PropTypes from 'prop-types';
import {
  CaretUpAshIcon, CaretDownAshIcon
} from '@glu/icons-react';
import { useClickOutside } from '@glu/utilities-react';
import useStyles from './HorizontalNav.styles';
import Menu from './components/Menu';

const HorizontalNav = (props) => {
  const {
    activeLink,
    children,
    className,
    CollapseLinkIcon,
    dataQa,
    ExpandLinkIcon,
    LinkComponent,
    navLinks,
    LeftRegion,
    RightRegion,
    openOnHover,
    primaryLogo,
    secondaryLogo,
    secondaryNavLinks
  } = props;

  const classes = useStyles(props);

  const [openNavItem, setOpenNavItem] = useState(activeLink);
  const ref = useRef();

  /* istanbul ignore next */
  const handleClose = () => {
    setOpenNavItem(null);
  };

  useClickOutside(ref, handleClose);

  return (
    <nav className={`${classes.root} ${className}`} data-qa={dataQa} ref={ref}>
      <div className={classes.left} id="leftSection">
        {(primaryLogo || secondaryLogo) && (
          <div className={classes.header}>
            {primaryLogo && (
            <div className={`${classes.logo} ${primaryLogo && secondaryLogo ? classes.logoDivider : ''}`}>
                {primaryLogo.length > 1
                  // eslint-disable-next-line react/no-array-index-key
                  ? primaryLogo.map((logo, index) => <div key={`primaryLogo-${index}`} className={classes[`primaryLogo-${index}`]}>{logo}</div>)
                  : primaryLogo}
            </div>
            )}
            {secondaryLogo && (
            <div className={classes.logo}>
                {secondaryLogo.length > 1
                  // eslint-disable-next-line react/no-array-index-key
                  ? secondaryLogo.map((logo, index) => <div key={`secondaryLogo-${index}`} className={classes[`secondaryLogo-${index}`]}>{logo}</div>)
                  : secondaryLogo}
            </div>
            )}
          </div>
        )}
        {children}
        {navLinks && (
        <ul className={classes.primaryNav} id="primaryNav">
            {navLinks.map((navLink, index) => {
              const id = navLink.id ? navLink.id : `primaryNav${index}`;
              return (
                <Menu
                  SubMenuComponent={navLink.menu}
                  CollapseIcon={CollapseLinkIcon}
                  dataQa={dataQa}
                  ExpandIcon={ExpandLinkIcon}
                  // eslint-disable-next-line react/no-array-index-key
                  key={`primaryNavigation-${index}`}
                  LinkComponent={LinkComponent}
                  open={openNavItem === id}
                  toggleOpen={() => { setOpenNavItem(id); }}
                  openOnHover={openOnHover}
                  {...navLink}
                  onClick={navLink.onClick}
                >
                  {navLink.children}
                </Menu>
              );
            })}
        </ul>
        )}
        {LeftRegion && (
        <div className={classes.leftRegion}>
          <LeftRegion />
        </div>
        )}
      </div>
      <div className={classes.right} id="rightSection">
        {RightRegion && (
        <div className={classes.rightRegion}>
          <RightRegion />
        </div>
        )}
        {secondaryNavLinks && (
          <ul className={classes.secondaryNav}>
            {secondaryNavLinks.map((navLink, index) => {
              const id = navLink.id ? navLink.id : `secondaryNav${index}`;
              return (
                <Menu
                  SubMenuComponent={navLink.menu}
                  alignRight
                  CollapseIcon={CollapseLinkIcon}
                  dataQa={dataQa}
                  ExpandIcon={ExpandLinkIcon}
                  // eslint-disable-next-line react/no-array-index-key
                  key={`secondaryNavigation-${index}`}
                  LinkComponent={LinkComponent}
                  open={openNavItem === id}
                  toggleOpen={() => { setOpenNavItem(id); }}
                  openOnHover={openOnHover}
                  {...navLink}
                  onClick={navLink.onClick}
                >
                  {navLink.children}
                </Menu>
              );
            })}
          </ul>
        )}
      </div>
    </nav>
  );
};

HorizontalNav.propTypes = {
  /** Expects a custom component to align left in the center white space of the nav bar */
  LeftRegion: PropTypes.func,
  /** Expects a custom component to align right in the center white space of the nav bar */
  RightRegion: PropTypes.func,
  /** unique id for the link to be active/open on load */
  activeLink: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** children are passed to horizontal nav container after the logo but before the links */
  children: PropTypes.node,
  /** classname added to the root wrapper for overriding styles */
  className: PropTypes.string,
  /** left logo. expects an image component */
  primaryLogo: PropTypes.node,
  /** Icon indicating that the children nav items are open and can be closed */
  CollapseLinkIcon: PropTypes.func,
  /** data attribute for easy targeting during qa testing */
  dataQa: PropTypes.string,
  /** icon indicating children of this link can be expanded */
  ExpandLinkIcon: PropTypes.func,
  /** custom link component.  */
  LinkComponent: PropTypes.func,
  /** right logo. expects an image component */
  secondaryLogo: PropTypes.node,
  /** array of objects with nav link data. left aligned */
  navLinks: PropTypes.arrayOf(PropTypes.shape({ closeNavOnClick: PropTypes.bool })),
  /** toggle if sub menus open on mouse over */
  openOnHover: PropTypes.bool,
  /** array of objects with nav link data. right aligned */
  secondaryNavLinks: PropTypes.arrayOf(PropTypes.shape({ closeNavOnClick: PropTypes.bool }))
};

HorizontalNav.defaultProps = {
  activeLink: undefined,
  children: undefined,
  RightRegion: undefined,
  LeftRegion: undefined,
  className: undefined,
  CollapseLinkIcon: CaretUpAshIcon,
  dataQa: undefined,
  ExpandLinkIcon: CaretDownAshIcon,
  LinkComponent: undefined,
  navLinks: [],
  openOnHover: true,
  primaryLogo: undefined,
  secondaryLogo: undefined,
  secondaryNavLinks: []
};

HorizontalNav.displayName = 'HorizontalNav';
export default HorizontalNav;
